import {
  ChartTooltipProps,
  getFilteredChartTooltipPayload,
} from "@mantine/charts"
import {
  Badge,
  Box,
  Divider,
  Flex,
  Group,
  Paper,
  Stack,
  Text,
} from "@mantine/core"
import { IconBookmarkFilled } from "@tabler/icons-react"
import _ from "lodash"
import { useCallback, useMemo } from "react"

import { EventsResponses } from "@costory/types/endpoints/events"
import { AggBy, Currency } from "@costory/types/prisma-client"

import { PREVIOUS_CHART_COLOR } from "@costory/shared/const"
import {
  BUSINESS_METRIC_NAME,
  BUSINESS_METRIC_TECHNICAL_NAME,
  tickFormatter,
} from "@costory/shared/utils/charts"
import { formatNumber } from "@costory/shared/utils/format"

import { IconsEventsCategory } from "@costory/front/utils/events"

export const EVENTS_NAME = "events"
const textStyles = {
  whiteSpace: "normal" as const,
  overflowWrap: "break-word" as const,
  wordBreak: "break-word" as const,
}

// Tooltip positioning constants
const TOOLTIP_POSITIONING = {
  DONUT_CHART_OFFSET: "-5vw",
  LEFT_ALIGN_TRANSFORM: "translateX(-110%)",
  NO_TRANSFORM: "none",
  ALERT_PREVIEW_THRESHOLD: 100,
  LABEL_EXPLORER_THRESHOLD: 300,
  DEFAULT_THRESHOLD: 300,
} as const

interface ExtendedChartTooltipProps extends Omit<ChartTooltipProps, "payload"> {
  currency: Currency
  tooltipData?: {
    groupByValue: string
    periodCost: number
    previousPeriodCost: number
    change: number
    relativeChange: number
  }[]
  coordinate?: { x: number }
  aggBy?: AggBy
  payload: Array<{
    name: string
    value: number
    dataKey: string
    color?: string
    payload?: Record<string, unknown>
  }>
  label: string
  displayEvents?: boolean
  events?: EventsResponses.Aggregated["businessEvents"]
  previousDate?: string
  groupByOptional?: boolean
  hideComparison?: boolean
  filterEventsByChangeDataPointPeriods?: boolean
  isDonutChart?: boolean
  isAlertPreview?: boolean
  isLabelexplorerChart?: boolean
  isDashboardWidget?: boolean
  showIntervalBars?: boolean
  isBarChart?: boolean
}

export const ChartTooltip = ({
  label,
  payload,
  tooltipData,
  currency,
  coordinate,
  aggBy,
  events,
  displayEvents = false,
  previousDate,
  groupByOptional,
  hideComparison,
  filterEventsByChangeDataPointPeriods,
  isDonutChart = false,
  isAlertPreview = false,
  isLabelexplorerChart = false,
  isDashboardWidget = false,
  showIntervalBars = false,
  isBarChart = false,
}: ExtendedChartTooltipProps) => {
  /**
   * Determines the CSS transform for tooltip positioning based on chart type and cursor position
   * @returns CSS transform string for tooltip positioning
   */
  const getTooltipTransform = (): string => {
    // Early return for donut charts or missing coordinates
    if (isDonutChart || !coordinate?.x) {
      return TOOLTIP_POSITIONING.DONUT_CHART_OFFSET
    }

    // Check for specific chart type thresholds
    const shouldAlignLeft =
      (isAlertPreview &&
        coordinate.x > TOOLTIP_POSITIONING.ALERT_PREVIEW_THRESHOLD) ||
      (isLabelexplorerChart &&
        coordinate.x > TOOLTIP_POSITIONING.LABEL_EXPLORER_THRESHOLD) ||
      isDashboardWidget ||
      coordinate.x > TOOLTIP_POSITIONING.DEFAULT_THRESHOLD

    if (shouldAlignLeft) {
      return TOOLTIP_POSITIONING.LEFT_ALIGN_TRANSFORM
    }

    // Default behavior: align based on screen center
    const screenCenter = window.innerWidth / 2
    return coordinate.x < screenCenter
      ? TOOLTIP_POSITIONING.NO_TRANSFORM
      : TOOLTIP_POSITIONING.LEFT_ALIGN_TRANSFORM
  }
  // When label is provided, it's the x axis value: if it's a date we will use it and format it, if it's a value (waterfall mode) we will use to filter the tooltipData and
  // Only keep the data required.
  const displayLabel = useMemo(
    () => label ?? payload?.[0]?.name ?? "",
    [label, payload],
  )
  const isLongLabel = (displayLabel.length ?? 0) > 25
  const isExcludedMetric = (name: string) =>
    name !== BUSINESS_METRIC_TECHNICAL_NAME &&
    name !== BUSINESS_METRIC_NAME &&
    name !== EVENTS_NAME

  const isValidTooltipItem = useCallback(
    (el: {
      name: string
      dataKey?: string
      payload?: Record<string, unknown>
    }) => {
      if (!isExcludedMetric(el.name)) return false
      // Filter out artificial forecast values (used for visual continuity only)
      if (el.name.endsWith("(forecast)")) {
        if (el.payload) {
          const dataKey = (el.dataKey as string) || el.name
          const artificialKey = `_${dataKey}_artificial`
          return !el.payload[artificialKey]
        }
      }
      return true
    },
    [],
  )

  const { totalValue, previousTotal } = useMemo(() => {
    const [previous, current] = _.partition(
      payload.filter(isValidTooltipItem),
      (el) => el.name.endsWith("(previous)"),
    )
    return {
      totalValue: _.sumBy(current, "value"),
      previousTotal: _.sumBy(previous, "value"),
    }
  }, [payload, isValidTooltipItem])

  const tooltipDataZoomed = useMemo(
    () =>
      _.find(tooltipData, {
        groupByValue: displayLabel,
      }),
    [displayLabel, tooltipData],
  )

  const forecastConfidence = useMemo(() => {
    const firstPayload = payload?.[0]?.payload
    if (
      !firstPayload ||
      !firstPayload.isForecast ||
      firstPayload.lower === null ||
      firstPayload.upper === null ||
      firstPayload.lower === undefined ||
      firstPayload.upper === undefined
    ) {
      return null
    }
    return {
      lower: firstPayload.lower as number,
      upper: firstPayload.upper as number,
    }
  }, [payload])

  if (!payload || payload.length === 0) return null
  const businessMetric = payload.find(
    (el) =>
      el.name === BUSINESS_METRIC_TECHNICAL_NAME ||
      el.name === BUSINESS_METRIC_NAME,
  )
  const index = previousDate && !hideComparison ? 1 : 0
  if (displayEvents && events) {
    const eventsThatDay = events.filter((el) => el.date === displayLabel)[0]

    if (eventsThatDay?.events) {
      // Separate bookmarked and non-bookmarked events
      const bookmarkedEvents = eventsThatDay.events.filter(
        (event) => event.isBookmarked,
      )
      const nonBookmarkedEvents = eventsThatDay.events.filter(
        (event) => !event.isBookmarked,
      )

      // Limit to 5 events total (bookmarked first, then non-bookmarked)
      const maxEvents = 5
      const displayBookmarked = bookmarkedEvents.slice(0, maxEvents)
      const remainingSlots = maxEvents - displayBookmarked.length
      const displayNonBookmarked = nonBookmarkedEvents.slice(0, remainingSlots)

      const totalDisplayed =
        displayBookmarked.length + displayNonBookmarked.length
      const hasMoreEvents = eventsThatDay.events.length > totalDisplayed

      return (
        <Paper w={400}>
          <Stack>
            <Text
              fw={700}
              fz="sm"
              maw={350}
              mr="md"
              style={tooltipDataZoomed ? textStyles : {}}
            >
              {filterEventsByChangeDataPointPeriods
                ? "Correlated events (click to associate)"
                : aggBy
                  ? tickFormatter(displayLabel, aggBy)
                  : displayLabel}
            </Text>
            <Divider />
            <Stack gap={1} justify="space-between">
              {/* Display bookmarked events first */}
              {displayBookmarked.map((el) => (
                <Group key={el.id} align="center">
                  <IconsEventsCategory category={el.category} />
                  <Text
                    style={{
                      ...textStyles,
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      maxWidth: "200px",
                    }}
                  >
                    {el.name}
                  </Text>
                  <IconBookmarkFilled size={14} color="warning" />
                </Group>
              ))}

              {/* Show divider if we have both bookmarked and non-bookmarked events */}
              {displayBookmarked.length > 0 &&
                displayNonBookmarked.length > 0 && <Divider my={4} />}

              {/* Display non-bookmarked events */}
              {displayNonBookmarked.map((el) => (
                <Group key={el.id} align="center">
                  <IconsEventsCategory category={el.category} />
                  <Text
                    style={{
                      ...textStyles,
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      maxWidth: "250px",
                    }}
                  >
                    {el.name}
                  </Text>
                </Group>
              ))}

              {hasMoreEvents && (
                <Text size="sm" c="dimmed">
                  + {eventsThatDay.events.length - totalDisplayed} more
                </Text>
              )}
            </Stack>
          </Stack>
        </Paper>
      )
    }

    return null
  }
  if (groupByOptional) {
    return (
      <Paper
        withBorder
        pos={coordinate ? "absolute" : "relative"}
        w={350}
        style={{
          left: coordinate?.x ?? 0,
          transform: getTooltipTransform(),
          zIndex: 1000,
        }}
      >
        {previousDate && !hideComparison && (
          <Flex mb="sm" justify="space-between" align="center">
            {renderGroup({
              color: PREVIOUS_CHART_COLOR,
              label: aggBy ? tickFormatter(previousDate, aggBy) : displayLabel,
            })}

            <Text fz="sm">
              {formatNumber({ value: previousTotal, currency, extraDigits: 2 })}
            </Text>
          </Flex>
        )}
        <Flex mb="sm" justify="space-between" align="center">
          {renderGroup({
            showBox: !tooltipDataZoomed,
            color: payload[index]?.color ?? "primaryHighlight",
            label: aggBy
              ? tickFormatter(displayLabel, aggBy)
              : formatNumber({ value: totalValue, currency, extraDigits: 2 }),
          })}

          <TooltipValueDisplay
            totalValue={totalValue}
            currency={currency}
            tooltipDataZoomed={tooltipDataZoomed}
          />
        </Flex>

        {businessMetric && (
          <>
            <Divider />
            <BusinessMetricDisplay value={businessMetric.value} />
          </>
        )}
        {forecastConfidence && showIntervalBars && isBarChart && (
          <>
            <Divider mt={8} />
            <Stack gap={4} mt={8}>
              <Text fw={700} fz="sm" c="dimmed">
                Forecast Confidence Interval
              </Text>
              <Flex justify="space-between" align="center">
                <Text fz="sm" c="dimmed">
                  Lower:
                </Text>
                <Text fz="sm">
                  {formatNumber({
                    value: forecastConfidence.lower,
                    currency,
                    extraDigits: 2,
                  })}
                </Text>
              </Flex>
              <Flex justify="space-between" align="center">
                <Text fz="sm" c="dimmed">
                  Upper:
                </Text>
                <Text fz="sm">
                  {formatNumber({
                    value: forecastConfidence.upper,
                    currency,
                    extraDigits: 2,
                  })}
                </Text>
              </Flex>
            </Stack>
          </>
        )}
      </Paper>
    )
  }

  return (
    <Paper
      withBorder
      pos={coordinate ? "absolute" : "relative"}
      w={350}
      style={{
        left: coordinate?.x ?? 0,
        transform: getTooltipTransform(),
        zIndex: 1000,
      }}
    >
      {isLongLabel ? (
        <Stack gap={0} mb="sm">
          <Text
            fw={700}
            fz="sm"
            maw={350}
            mr="md"
            style={{
              ...(tooltipDataZoomed ? textStyles : {}),
            }}
          >
            {aggBy ? tickFormatter(displayLabel, aggBy) : displayLabel}
          </Text>
          <Group justify="flex-end" mt={4}>
            <TooltipValueDisplay
              totalValue={totalValue}
              currency={currency}
              tooltipDataZoomed={tooltipDataZoomed}
            />
          </Group>
        </Stack>
      ) : (
        <Flex
          mb="sm"
          justify="space-between"
          align="center"
          style={{ maxWidth: 350 }}
        >
          <Text fw={700} fz="sm" mr="md" style={textStyles}>
            {aggBy ? tickFormatter(displayLabel, aggBy) : displayLabel}
          </Text>
          <TooltipValueDisplay
            totalValue={totalValue}
            currency={currency}
            tooltipDataZoomed={tooltipDataZoomed}
          />
        </Flex>
      )}
      <Divider />
      {getFilteredChartTooltipPayload(payload)
        .filter((el) =>
          isValidTooltipItem(
            el as {
              name: string
              dataKey?: string
              payload?: Record<string, unknown>
            },
          ),
        )
        .sort((a, b) => b.value - a.value)
        .map(({ name, value, color, payload }) => (
          <Flex key={name} align="center" gap={8} mt={8}>
            <Box
              w={8}
              h={8}
              style={{
                borderRadius: 4,
                background: color || payload.fill,
              }}
            />
            <Box style={{ flex: 1 }}>
              <Text fw={700} mr="sm" style={textStyles} fz="sm">
                {name}
              </Text>
            </Box>
            <Text fz="sm">
              {formatNumber({ value, currency, extraDigits: 2 })}
            </Text>
          </Flex>
        ))}

      {businessMetric && <BusinessMetricDisplay value={businessMetric.value} />}
      {forecastConfidence && showIntervalBars && isBarChart && (
        <>
          <Divider mt={8} />
          <Stack gap={4} mt={8}>
            <Text fw={700} fz="sm" c="dimmed">
              Forecast Confidence Interval
            </Text>
            <Flex justify="space-between" align="center">
              <Text fz="sm" c="dimmed">
                Lower:
              </Text>
              <Text fz="sm">
                {formatNumber({
                  value: forecastConfidence.lower,
                  currency,
                  extraDigits: 2,
                })}
              </Text>
            </Flex>
            <Flex justify="space-between" align="center">
              <Text fz="sm" c="dimmed">
                Upper:
              </Text>
              <Text fz="sm">
                {formatNumber({
                  value: forecastConfidence.upper,
                  currency,
                  extraDigits: 2,
                })}
              </Text>
            </Flex>
          </Stack>
        </>
      )}
    </Paper>
  )
}

type TooltipValueDisplayProps = {
  totalValue: number
  currency: string
  tooltipDataZoomed?: { relativeChange?: number }
}

const TooltipValueDisplay = ({
  totalValue,
  currency,
  tooltipDataZoomed,
}: TooltipValueDisplayProps) => {
  if (tooltipDataZoomed?.relativeChange !== undefined) {
    return (
      <Badge
        size="lg"
        color={
          tooltipDataZoomed?.relativeChange &&
          tooltipDataZoomed.relativeChange > 0
            ? "red.5"
            : "green.5"
        }
      >
        {formatNumber({
          value: tooltipDataZoomed.relativeChange,
          numberType: "percent",
          currency,
          extraDigits: 2,
        })}
      </Badge>
    )
  }

  return (
    <Text fz="sm">
      {formatNumber({ value: totalValue, currency, extraDigits: 2 })}
    </Text>
  )
}

const BusinessMetricDisplay = ({ value }: { value: number }) => {
  return (
    <Group key="Metric" gap={1} mt={8} justify="space-between" wrap="nowrap">
      <Text fw={700} mr="sm" fz="sm">
        Metric
      </Text>
      <Text fz="sm">
        {formatNumber({ value, numberType: "decimal", extraDigits: 2 })}
      </Text>
    </Group>
  )
}

const renderGroup = ({
  color,
  label,
  showBox = true,
}: {
  color: string
  label: string
  showBox?: boolean
}) => (
  <Group gap={8} wrap="nowrap" mr={30}>
    {showBox && <Box w={8} h={8} style={{ borderRadius: 4 }} bg={color} />}
    <Text fw={700} fz="sm" maw={350} mr="md">
      {label}
    </Text>
  </Group>
)
